import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchEvaluationTiltak } from "../../../../api/hse/evaluations";
import { EvaluationLabelInfo } from "./EvaluationLabelInfo";
import { CreateTiltakDialog } from "./CreateTiltakDialog";
import { personLabel, tiltakCreatePath, tiltakViewPath } from "./evaluationUtils";

export function EvaluationFindingsPanel({ evaluation, onRefresh }) {
  const { t } = useTranslation();
  const [tiltak, setTiltak] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadTiltak = async () => {
    if (!evaluation?.id) {
      setTiltak([]);
      return;
    }
    try {
      const rows = await fetchEvaluationTiltak(evaluation.id);
      setTiltak(rows);
    } catch (_err) {
      setTiltak([]);
    }
  };

  useEffect(() => {
    loadTiltak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation?.id]);

  const findings = evaluation?.findings || "";

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,.04)]">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[16px] font-extrabold text-slate-900">
          <EvaluationLabelInfo label={t("hse.evaluation.findings.title")} infoKey="finding" />
        </h3>
        {evaluation?.id ? (
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="text-sm font-semibold text-[#1463C7] hover:underline"
          >
            {t("hse.evaluation.findings.create_tiltak")}
          </button>
        ) : null}
      </div>

      {findings ? (
        <p className="whitespace-pre-line text-sm leading-6 text-slate-700">{findings}</p>
      ) : (
        <p className="text-sm text-slate-500">{t("hse.evaluation.findings.empty")}</p>
      )}

      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[13px] font-bold text-slate-700">
            <EvaluationLabelInfo label={t("hse.evaluation.tiltak")} infoKey="tiltak" />
          </p>
          {evaluation?.id ? (
            <Link
              to={tiltakCreatePath({ evaluationId: evaluation.id })}
              className="text-sm font-semibold text-[#1463C7] hover:underline"
            >
              {t("hse.evaluation.tiltak.open_page")}
            </Link>
          ) : null}
        </div>
        {tiltak.length === 0 ? (
          <p className="text-sm text-slate-500">{t("hse.evaluation.tiltak.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tiltak.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <Link
                    to={tiltakViewPath(item.id)}
                    className="text-sm font-semibold text-slate-900 hover:text-[#1463C7]"
                  >
                    {item.title}
                  </Link>
                  <p className="text-xs text-slate-500">
                    {[personLabel(item.responsible_user), item.deadline, item.status]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <Link
                  to={tiltakViewPath(item.id)}
                  className="text-xs font-semibold text-[#1463C7] hover:underline"
                >
                  {t("hse.evaluation.tiltak.open_existing")}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CreateTiltakDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        evaluation={evaluation}
        finding={{ title: evaluation?.title, description: findings }}
        onCreated={() => {
          loadTiltak();
          onRefresh?.();
        }}
      />
    </section>
  );
}
